import { useState, useEffect } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil, X, Home } from "lucide-react";
import { Link } from "react-router-dom";
import { useApp, api } from "../AppContext";
import { OmMark } from "../components/Motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const RESOURCES = {
  news: { label: "News", fields: [
    { k: "title_mr", l: "Title (Marathi)" }, { k: "title_en", l: "Title (English)" },
    { k: "body_mr", l: "Body (Marathi)", area: true }, { k: "body_en", l: "Body (English)", area: true },
    { k: "date", l: "Date" }, { k: "pinned", l: "Pinned", bool: true } ] },
  festivals: { label: "Festivals", fields: [
    { k: "name_mr", l: "Name (Marathi)" }, { k: "name_en", l: "Name (English)" },
    { k: "month_mr", l: "Month (Marathi)" }, { k: "month_en", l: "Month (English)" }, { k: "date", l: "Date" },
    { k: "desc_mr", l: "Description (Marathi)", area: true }, { k: "desc_en", l: "Description (English)", area: true } ] },
  gallery: { label: "Gallery", fields: [
    { k: "url", l: "Image URL" }, { k: "caption_mr", l: "Caption (Marathi)" }, { k: "caption_en", l: "Caption (English)" }, { k: "category", l: "Category" } ] },
  tariffs: { label: "Tariffs", fields: [
    { k: "room_mr", l: "Room (Marathi)" }, { k: "room_en", l: "Room (English)" }, { k: "beds", l: "Beds" }, { k: "ac", l: "AC / Non-AC" }, { k: "price", l: "Price" } ] },
};

function Login() {
  const { login } = useApp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setErr("");
    try { await login(email, password); toast.success("Welcome, Trust Admin"); }
    catch (e2) { const d = e2.response?.data?.detail; setErr(typeof d === "string" ? d : "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-earth-900 px-4">
      <form onSubmit={submit} className="w-full max-w-md rounded-3xl bg-sand-50 p-8 border border-earth-900/10" data-testid="admin-login-form">
        <div className="flex items-center gap-3 text-deepred-700 mb-6"><OmMark className="w-10 h-10" /><h1 className="font-serif-head text-2xl text-earth-900">Trust Admin</h1></div>
        <div className="space-y-4">
          <div><Label className="mb-2 block font-semibold">Email</Label><Input type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 border-earth-900/20" /></div>
          <div><Label className="mb-2 block font-semibold">Password</Label><Input type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 border-earth-900/20" /></div>
          {err && <p className="text-deepred-700 text-sm" data-testid="admin-error">{err}</p>}
          <Button type="submit" disabled={loading} data-testid="admin-login-btn" className="w-full h-12 bg-saffron-500 hover:bg-saffron-600 text-white font-semibold">{loading ? "..." : "Login"}</Button>
        </div>
        <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-earth-600"><Home size={14} /> Back to site</Link>
      </form>
    </div>
  );
}

function ResourceManager({ name }) {
  const cfg = RESOURCES[name];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/${name}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [name]);

  const blank = () => { const o = {}; cfg.fields.forEach((f) => (o[f.k] = f.bool ? false : "")); return o; };
  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/${name}/${editing.id}`, editing);
      else await api.post(`/admin/${name}`, editing);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; try { await api.delete(`/admin/${name}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <h2 className="font-serif-head text-2xl text-earth-900">{cfg.label} ({items.length})</h2>
        <Button onClick={() => setEditing(blank())} data-testid={`add-${name}-btn`} className="bg-saffron-500 hover:bg-saffron-600 text-white"><Plus size={18} /> Add</Button>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <div key={it.id} className="flex items-start justify-between gap-4 rounded-xl border border-earth-900/10 bg-white p-4" data-testid={`${name}-item`}>
            <div className="min-w-0">
              <div className="font-semibold text-earth-900 truncate">{it.title_en || it.name_en || it.room_en || it.caption_en || it.url}</div>
              <div className="text-sm text-earth-600 truncate">{it.title_mr || it.name_mr || it.room_mr || it.price || it.category}</div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(it)} className="p-2 text-earth-700 hover:text-saffron-600" data-testid={`edit-${name}`}><Pencil size={18} /></button>
              <button onClick={() => del(it.id)} className="p-2 text-earth-700 hover:text-deepred-700" data-testid={`delete-${name}`}><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-earth-900/70 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-sand-50 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={`${name}-editor`}>
            <div className="flex justify-between items-center mb-4"><h3 className="font-serif-head text-xl">{editing.id ? "Edit" : "Add"} {cfg.label}</h3><button onClick={() => setEditing(null)}><X /></button></div>
            <div className="space-y-3">
              {cfg.fields.map((f) => (
                <div key={f.k}>
                  <Label className="mb-1 block text-sm font-semibold">{f.l}</Label>
                  {f.bool ? (
                    <input type="checkbox" checked={!!editing[f.k]} onChange={(e) => setEditing({ ...editing, [f.k]: e.target.checked })} data-testid={`field-${f.k}`} className="w-5 h-5" />
                  ) : f.area ? (
                    <Textarea value={editing[f.k] || ""} onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })} data-testid={`field-${f.k}`} className="border-earth-900/20" />
                  ) : (
                    <Input value={editing[f.k] || ""} onChange={(e) => setEditing({ ...editing, [f.k]: e.target.value })} data-testid={`field-${f.k}`} className="h-11 border-earth-900/20" />
                  )}
                </div>
              ))}
            </div>
            <Button onClick={save} data-testid={`save-${name}-btn`} className="mt-5 w-full h-11 bg-saffron-500 hover:bg-saffron-600 text-white">Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Enquiries() {
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
  }, []);
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <h2 className="font-serif-head text-2xl text-earth-900 mb-4">Bookings ({bookings.length})</h2>
        <div className="space-y-3">{bookings.map((b) => (
          <div key={b.id} className="rounded-xl border border-earth-900/10 bg-white p-4 text-sm" data-testid="booking-item">
            <div className="font-semibold">{b.name} — {b.phone}</div>
            <div className="text-earth-600">{b.email}</div>
            <div>{b.room_type} | {b.guests} guests | {b.checkin} → {b.checkout}</div>
            {b.message && <div className="text-earth-600 mt-1">{b.message}</div>}
          </div>))}
          {bookings.length === 0 && <p className="text-earth-500">No bookings yet.</p>}
        </div>
      </div>
      <div>
        <h2 className="font-serif-head text-2xl text-earth-900 mb-4">Messages ({contacts.length})</h2>
        <div className="space-y-3">{contacts.map((c) => (
          <div key={c.id} className="rounded-xl border border-earth-900/10 bg-white p-4 text-sm" data-testid="contact-item">
            <div className="font-semibold">{c.name} — {c.phone}</div>
            <div className="text-earth-600">{c.email}</div>
            <div className="mt-1">{c.message}</div>
          </div>))}
          {contacts.length === 0 && <p className="text-earth-500">No messages yet.</p>}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { isAuthed, logout } = useApp();
  if (!isAuthed) return <Login />;
  return (
    <div className="min-h-screen bg-sand-100">
      <header className="bg-earth-900 text-sand-100 sticky top-0 z-40">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><OmMark className="w-8 h-8 text-ochre-400" /><span className="font-serif-head text-xl">Trust Admin Panel</span></div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm hover:text-ochre-400 inline-flex items-center gap-1"><Home size={16} /> Site</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="inline-flex items-center gap-1 text-sm hover:text-saffron-400"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-10">
        <Tabs defaultValue="news">
          <TabsList className="flex flex-wrap h-auto bg-sand-50 border border-earth-900/10">
            {Object.keys(RESOURCES).map((k) => <TabsTrigger key={k} value={k} data-testid={`tab-${k}`} className="data-[state=active]:bg-saffron-500 data-[state=active]:text-white">{RESOURCES[k].label}</TabsTrigger>)}
            <TabsTrigger value="enquiries" data-testid="tab-enquiries" className="data-[state=active]:bg-saffron-500 data-[state=active]:text-white">Enquiries</TabsTrigger>
          </TabsList>
          {Object.keys(RESOURCES).map((k) => <TabsContent key={k} value={k} className="mt-6"><ResourceManager name={k} /></TabsContent>)}
          <TabsContent value="enquiries" className="mt-6"><Enquiries /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
